import {
    TravelFile,
    TravelFilePassenger,
    TravelFileService,
    TravelFileServicePassenger
} from '../../../api/travelFiles/oldModelTypes';
import {
    TravelFileTypes,
    travelFileTypesEnum
} from '../../../api/travelFiles/constants/travelFileTypesOptions';
import {
    TravelFileServiceTypes,
    travelFileServiceTypesEnum
} from '../../../api/services/constants/travelFileService';
import { baseServiceStatusTypesEnum } from '../../../api/schemas/serviceStatusTypes';
import {
    TravelFileStatusTypes,
    TravelFileStatusTypesEnum
} from '../../../api/services/constants/travelFileStatus';
import { ServiceStatusTypes } from '../../../api/services/constants/ServiceStatusTypes';

export const getTravelFileByType = (travelFile: TravelFile, options?: { originalService: boolean }) => {
    switch (travelFile.type) {
        case travelFileTypesEnum.GROUP: {
            const relatedGroupTravelFiles = travelFile.relatedGroupTravelFiles;
            // PG returns passengers/services directly; MongoDB used relatedGroupTravelFiles.
            // Fall back to direct data when relatedGroupTravelFiles is not populated.
            // Check if relatedGroupTravelFiles have passengers loaded (lightweight PG load may not)
            const hasRelatedPassengers = relatedGroupTravelFiles?.some(
                (tf) => tf.passengers?.length > 0
            );
            const passengers = hasRelatedPassengers
                ? getGroupPassengers(relatedGroupTravelFiles, travelFile)
                : (travelFile.passengers || []).map((p) => ({
                      ...p,
                      passengerTravelFileRefNumber: p.passengerTravelFileRefNumber || travelFile.refNumber
                  }));
            const servicesObj = options?.originalService
                ? {}
                : {
                      services: hasRelatedPassengers
                          ? getGroupServices(relatedGroupTravelFiles)
                          : travelFile.services || []
                  };
            return { ...travelFile, passengers, ...servicesObj };
        }
        case travelFileTypesEnum.CLIENT: {
            return travelFile;
        }
        default: {
            return travelFile;
        }
    }
};
type RelatedGroupTravelFiles = TravelFile['relatedGroupTravelFiles'] | undefined;

export const getGroupPassengers = (
    relatedGroupTravelFiles: RelatedGroupTravelFiles,
    groupTravelFile?: TravelFile
) => {
    // passengerTravelFileStatus is the status of the CLIENT file the passenger
    // sits on, not the group's. This is the branch the group Passengers grid
    // actually takes (getTravelFileByType prefers relatedGroupTravelFiles over
    // the group's own passengers array), so without the stamp here the grid's
    // Cancelled / In Process filters have nothing to read — the group row's own
    // status would be the same value for every passenger.
    const fromClients = (relatedGroupTravelFiles || []).reduce<TravelFilePassenger[]>((acc, next) => {
        const arr =
            (next.passengers || []).map((passenger) => {
                return {
                    ...passenger,
                    passengerTravelFileRefNumber: next.refNumber,
                    passengerTravelFileStatus: next.status
                };
            });

        return acc.concat(arr);
    }, []);

    // Some passengers live only on the group TF (no per-passenger client TF
    // in the same group). TB still shows them on the group's services, so
    // mirror that — fall back to the group TF's own passengers list and
    // merge in only those not already covered by a client TF.
    const groupOwnPassengers = groupTravelFile?.passengers;
    if (!groupOwnPassengers?.length) return fromClients;

    const getPassengerId = (p: any): string | undefined => p?.info?._id || p?.passengerId;
    const seen = new Set(fromClients.map(getPassengerId).filter(Boolean) as string[]);
    const groupOnly = groupOwnPassengers
        .filter((p) => {
            const pid = getPassengerId(p);
            return pid && !seen.has(pid);
        })
        .map((p) => ({
            ...p,
            passengerTravelFileRefNumber:
                (p as any).passengerTravelFileRefNumber || groupTravelFile.refNumber
        }));
    return fromClients.concat(groupOnly);
};

/**
 * The fields that decide whether a passenger is a live, real booking.
 *
 * All three are optional because two of them only exist in some contexts:
 * `passengerTravelFileStatus` is stamped per row for GROUP travel files only
 * (see getGroupPassengers and PASSENGER_STATUS_COLUMN on the server) — on a
 * client file every row shares the file's own status, so it is left off and
 * the status predicates below are no-ops there. `isFictive` comes from the
 * passengers table and survives getTravelFilePassengerFlatten's spread.
 */
type PassengerVisibilityFields = {
    passengerTravelFileStatus?: TravelFileStatusTypes;
    isPassengerRemoveFromTravelFile?: boolean;
    isFictive?: boolean;
};

/**
 * Cancelled in either of its two senses: the passenger was taken off this
 * travel file, or the client file they sit on was itself called off.
 */
export const isPassengerCancelled = (passenger: PassengerVisibilityFields) =>
    passenger.isPassengerRemoveFromTravelFile === true ||
    passenger.passengerTravelFileStatus === TravelFileStatusTypesEnum.CANCELLED;

/**
 * Their client travel file was created but never paid for — an abandoned
 * tour-registration checkout, or a booking still being keyed in.
 */
export const isPassengerInProcess = (passenger: PassengerVisibilityFields) =>
    passenger.passengerTravelFileStatus === TravelFileStatusTypesEnum.IN_PROCESS;

/**
 * A placeholder holding a seat so a tour does not read as empty — not a real
 * person. Already excluded by default from the dashboard counts and the
 * Cancelled Passengers report (COALESCE(p."isFictive", false) = false).
 */
export const isPassengerFictive = (passenger: PassengerVisibilityFields) =>
    passenger.isFictive === true;

/**
 * A real person, on a live booking, still on the file — the default audience
 * for anything that counts passengers or prints them.
 *
 * The Passengers grid can override each clause with its own Show Cancelled /
 * Show In Process / Show Fictive checkboxes. Edit Rooms and the travel file
 * documents apply it unconditionally: rooming is an allocation, and a printed
 * list goes to a hotel or a guide who needs the people who are actually
 * coming.
 */
export const isPassengerLive = (passenger: PassengerVisibilityFields) =>
    !isPassengerCancelled(passenger) &&
    !isPassengerInProcess(passenger) &&
    !isPassengerFictive(passenger);

/**
 * Resolve a passenger row's Travel Agent.
 *
 * The agent is a property of the BOOKING, never of the person — the same
 * passenger can be sold by agent A on one travel file and agent B on the next.
 * `passengers.agent` exists with an FK to travelagencyaccounts and is 100% NULL
 * by design; populating it was rejected because 187 customers booked through
 * more than one agent, and one column cannot hold two. Anything reading
 * `passenger.agent` therefore renders blank on every row, which is
 * indistinguishable from the (common) case of a booking with no agent at all.
 *
 * So resolve it from the file the row belongs to: on a GROUP travel file that
 * is the owning client sub-file, found by the same refNumber the Source column
 * links to, because a group file never carries an agent of its own. On a client
 * file it is simply that file's own agent.
 *
 * Returns a lookup rather than stamping a field onto passenger rows: in the
 * Passengers grid those rows are spread into the save payload, so an extra key
 * would reach the UPDATE.
 */
export const makeTravelAgentResolver = (
    relatedGroupTravelFiles: RelatedGroupTravelFiles,
    selectedTravelFile: any
): ((passengerTravelFileRefNumber?: string | null) => string) => {
    // Group sub-files arrive with the name already joined
    // (travelAgencyAccountName); a populated account object, array-wrapped or
    // not, is the other shape the API returns.
    const nameOf = (travelFile: any): string => {
        const agentField = travelFile?.travelAgencyAccount;
        const populated = Array.isArray(agentField) ? agentField[0] : agentField;
        return travelFile?.travelAgencyAccountName || populated?.companyName || '';
    };

    const byRefNumber: Record<string, string> = {};
    (relatedGroupTravelFiles || []).forEach((clientTf: any) => {
        const name = nameOf(clientTf);
        if (clientTf?.refNumber && name) byRefNumber[clientTf.refNumber] = name;
    });

    const ownAgentName = nameOf(selectedTravelFile);

    return (passengerTravelFileRefNumber) =>
        byRefNumber[passengerTravelFileRefNumber || ''] || ownAgentName || '';
};

export const getGroupServices = (relatedGroupTravelFiles: RelatedGroupTravelFiles) =>
    (relatedGroupTravelFiles || []).reduce((acc, next) => {
        return acc.concat(next.services || []);
    }, [] as Array<TravelFileService>);

type GetTravelFileIdByPassengerProps = {
    travelFileType: TravelFileTypes;
    selectedTravelFile: TravelFile | null;
    passengerId: string;
};

export const getTravelFileIdByPassengerId = ({
    travelFileType,
    selectedTravelFile,
    passengerId
}: GetTravelFileIdByPassengerProps) => {
    switch (travelFileType) {
        case travelFileTypesEnum.GROUP: {
            const gtf = getGroupRelatedTravelFileByPassengerId(
                selectedTravelFile?.relatedGroupTravelFiles,
                passengerId
            );
            return gtf?._id || '';
        }
        case travelFileTypesEnum.CLIENT: {
            return selectedTravelFile?._id || '';
        }
        default: {
            return '';
        }
    }
};
export const getTravelFileServiceByPassengerId = ({
    travelFileType,
    selectedTravelFile,
    passengerId
}: GetTravelFileIdByPassengerProps) => {
    switch (travelFileType) {
        case travelFileTypesEnum.GROUP: {
            const gtf = getGroupRelatedTravelFileByPassengerId(
                selectedTravelFile?.relatedGroupTravelFiles,
                passengerId
            );
            return gtf?.services || [];
        }
        case travelFileTypesEnum.CLIENT: {
            return selectedTravelFile?.services || [];
        }
        default: {
            return [];
        }
    }
};

export const getGroupRelatedTravelFileByPassengerId = (
    relatedGroupTravelFiles: TravelFile[] | undefined,
    passengerId: string
): TravelFile | undefined => {
    if (!passengerId) {
        return;
    }
    return relatedGroupTravelFiles?.find((relatedTravelFile) =>
        relatedTravelFile.passengers.some((relatedTfPass) => {
            // PG flat shape uses `passengerId` directly; legacy Mongo
            // shape nested it under `info` (object _id, or string).
            const relatedTfPassId =
                (relatedTfPass as any).passengerId ??
                (typeof relatedTfPass.info === 'object'
                    ? relatedTfPass.info._id
                    : relatedTfPass.info);
            return String(relatedTfPassId) === String(passengerId);
        })
    );
};

export const checkIsTourOrHotelService = (
    serviceType: TravelFileServiceTypes,
    serviceStatus?: ServiceStatusTypes
) => {
    return (
        (serviceType === travelFileServiceTypesEnum.TOURS ||
            serviceType === travelFileServiceTypesEnum.HOTELS) &&
        serviceStatus !== baseServiceStatusTypesEnum.CANCELLED
    );
};

export const getFirstTourOrHotelService = (services?: TravelFileService[]): TravelFileService | undefined => {
    return services?.find((service) => checkIsTourOrHotelService(service.type, service?.info?.status));
};

export function getFirstTourOrHotelServiceOrServicesPassengers({
    travelFileType,
    relatedGroupTravelFiles,
    services
}: {
    travelFileType: TravelFileTypes;
    relatedGroupTravelFiles: RelatedGroupTravelFiles;
    services: TravelFileService[];
}): TravelFileServicePassenger[] {
    switch (travelFileType) {
        case travelFileTypesEnum.GROUP: {
            return (relatedGroupTravelFiles || []).reduce((acc, next: TravelFile) => {
                const firstTourOrHotelService = getFirstTourOrHotelService(next.services);
                const arrPass = (firstTourOrHotelService?.passengers || []).map((passenger) => ({
                    ...passenger,
                    passengerTravelFileRefNumber: next.refNumber,
                    // Edit Rooms drops In Process registrations using this — see
                    // EditRoomsGroupTravelFileModal. Stamped on both branches
                    // (service-level here, TF-level in getGroupPassengers) so the
                    // exclusion holds whichever one the modal falls back to.
                    passengerTravelFileStatus: next.status
                }));

                return acc.concat(arrPass || []);
            }, [] as Array<TravelFileServicePassenger>);
        }
        case travelFileTypesEnum.CLIENT: {
            return getFirstTourOrHotelService(services)?.passengers || [];
        }
        default: {
            return [];
        }
    }
}
